export type ChartPoint = {
  label: string
  value: number
}

function hashString(input: string): number {
  let hash = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function seededRandom(seed: number): () => number {
  let state = seed
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 4294967296
  }
}

function timeBucket(minutes = 5): number {
  return Math.floor(Date.now() / (minutes * 60_000))
}

export type BuildChartSeriesOptions = {
  points?: number
  base?: number
  min?: number
  max?: number
  stepAmplitude?: number
  decimals?: number
  tick?: number
}

export function buildChartSeries(
  seedKey: string,
  options: BuildChartSeriesOptions = {}
): ChartPoint[] {
  const {
    points = 12,
    base = 50,
    min = 5,
    max = 95,
    stepAmplitude = 8,
    decimals = 1,
    tick = timeBucket(),
  } = options

  const rand = seededRandom(hashString(seedKey) + tick)
  const series: ChartPoint[] = []
  let value = base + rand() * 20

  for (let i = 0; i < points; i += 1) {
    value += (rand() - 0.45) * stepAmplitude
    value = Math.max(min, Math.min(max, value))
    series.push({
      label: `${i + 1}`,
      value: Number(value.toFixed(decimals)),
    })
  }

  return series
}

export function buildPriceChartSeries(
  seedKey: string,
  options: { points?: number; base?: number; tick?: number } = {}
): ChartPoint[] {
  const { points = 7, base = 1.0002, tick = timeBucket(1) } = options
  const labels = ["00:00", "04:00", "08:00", "12:00", "16:00", "20:00", "Now"]
  const rand = seededRandom(hashString(seedKey) + tick)
  const series: ChartPoint[] = []
  let value = base + (rand() - 0.5) * 0.0004

  for (let i = 0; i < points; i += 1) {
    value += (rand() - 0.5) * 0.0003
    value = Math.max(0.9995, Math.min(1.001, value))
    series.push({
      label: labels[i] ?? `${i + 1}`,
      value: Number(value.toFixed(4)),
    })
  }

  return series
}

export function buildScoreTrendSeries(
  seedKey: string,
  options: { points?: number; base?: number; tick?: number } = {}
): ChartPoint[] {
  const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
  const { points = 7, base = 92, tick = timeBucket(2) } = options

  return buildChartSeries(seedKey, {
    points,
    base,
    min: 80,
    max: 99,
    stepAmplitude: 4,
    decimals: 0,
    tick,
  }).map((point, index) => ({
    ...point,
    label: labels[index] ?? point.label,
  }))
}
